import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDefinition, RowComponent } from "tabulator-tables";
import { Users } from "../../api/account/Users";
import Modal from "../../components/Modal";
import Tabulator, { EventsType } from "../../components/Tabulator";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";
import { usePortfolioUser, userSchema } from "./Hooks";
import { CountrySelect } from "./components/CountrySelect";
import { useAuth } from "../../hooks/useAuth";
import { PortfolioPermissions } from "../../api/compute/PortfolioPermissions";
import styles from "./PortfoliosUsersManagement.module.scss";

type UserListProps = {
  users: any;
  onRowClick: (event, row: RowComponent) => void;
  onClickCreateBtn: () => void;
  filters: {
    type: string;
    profile: string;
    contractType: string;
    active: string;
    owner: string;
    productMax: number | string;
  };
  updateFilters: (filters: {
    type: string;
    profile: string;
    contractType: string;
    active: string;
    owner: string;
    productMax: number | string;
  }) => void;
};

type FiltersRowProps = {
  filters: {
    type: string;
    profile: string;
    contractType: string;
    active: string;
    owner: string;
    productMax: number | string;
  };
  handleChangeFilters: (filters: {
    type: string;
    profile: string;
    contractType: string;
    active: string;
    owner: string;
    productMax: number | string;
  }) => void;
};

type CreateNewUserPanelProps = {
  handleClose: (reload?: boolean) => void;
  operationType: "CREATE" | "UPDATE" | "CONVERT";
  existingUser?: any;
};

const TABS = [
  { label: "Users", value: "users", icon: <PersonIcon /> },
  { label: "Pending Users", value: "pending", icon: <QueryBuilderIcon />},
];

const WIDGETS_OPTIONS = {
  accountType: [
    { value: "unset", label: "Choose an option" },
    { value: "INTERNAL", label: "Internal" },
    { value: "SUBSCRIBER", label: "Subscriber" },
    { value: "PARTNER", label: "Partner" },
    { value: "TRIAL", label: "Trial" },
  ],
  owner: [
    { value: "unset", label: "Choose an option" },

    { label: "Coulter John", value: "Coulter John" },
    { label: "Jordan Roland", value: "Jordan Roland" },
    { label: "Marinelli Antonio", value: "Marinelli Antonio" },
    { label: "Nathwani Shiv", value: "Nathwani Shiv" },
    {
      label: "Pellegrinelli Massimiliano",
      value: "Pellegrinelli Massimiliano",
    },
    { label: "Pellegrinelli Guido", value: "Pellegrinelli Guido" },
    { label: "Montanaro Andrea", value: "Montanaro Andrea" },
    { label: "Admin", value: "Admin" },
  ],
  profile: [
    { value: "unset", label: "Choose an option" },
    { value: "Porfolio_Manager", label: "Porfolio Manager" },
    { value: "Fund_Manager", label: "Fund Manager" },
    { value: "Wealth_Manager", label: "Wealth Manager" },
    { value: "Financial_Advisor", label: "Financial Advisor" },
    { value: "Hedge_Fund_Manager", label: "Hedge Fund Manager" },
    { value: "Family_office", label: "Family office" },
    { value: "Broker", label: "Broker" },
    { value: "Others", label: "Others" },
  ],

  contractType: [
    { value: "unset", label: "Choose an option" },
    { value: "Performance", label: "Performance" },
    { value: "Fixed", label: "Fixed" },
  ],
  active: [
    { value: "unset", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ],
  portfolioNumber: [
    { label: "All", value: 100 },
    { label: "one", value: 1 },
    { label: "three", value: 3 },
    { label: "ten", value: 10 },
  ],
};

export default function PortfoliosUsersManagement() {
  const [showTab, setShowTab] = useState("users");
  const [crudOperationType, setCrudOperationType] = useState<
    "CREATE" | "UPDATE" | "CONVERT"
  >();
  const [filters, setFilters] = useState<{
    type: string;
    profile: string;
    contractType: string;
    active: string;
    owner: string;
    productMax: number | string;
  }>({
    type: "unset",
    profile: "unset",
    contractType: "unset",
    active: "unset",
    owner: "unset",
    productMax: 100,
  });
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [refreshPendingTrigger, setRefreshPendingTrigger] = useState(
    Date.now()
  );

  const usersAPI = useMemo(() => new Users(), []);
  const feedback = useFeedbackSystem();

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: any) => {
      setShowTab(value);

      if (value === "users") {
      }
    },
    []
  );

  const makeFilters = useCallback((filters: any) => {
    let filtersList: any = [];

    for (const key in filters) {
      if (filters[key] === "unset" || filters[key] === 100) {
        continue;
      }

      if (key !== "active") {
        filtersList.push({ dimension: key, segments: [filters[key]] });
      } else {
        filtersList.push({
          dimension: key,
          segments: [filters[key] === "active"],
        });
      }
    }

    return filtersList;
  }, []);

  const getUsers = useCallback(
    async (filters?) => {
      try {
        const response = await usersAPI.getUserOnline(filters ?? []);

        setUsers(response?.data?.rows);
      } catch (error) {
        console.log(error);

        feedback.show({
          type: "error",
          message: error as any,
        });
      }
    },
    [feedback, usersAPI]
  );

  const handleFiltersChange = useCallback(
    (filters) => {
      setFilters(filters);
      const filtersList = makeFilters(filters);
      getUsers(filtersList);
    },
    [getUsers, makeFilters]
  );

  const refreshPendingUsers = useCallback(() => {
    setRefreshPendingTrigger(Date.now());
  }, []);

  const handleUsersTablePendingRowClick = useCallback((event, row) => {
    const user = row.getData();

    const schema = JSON.parse(JSON.stringify(userSchema));

    // Ensures that missing fields will correctly exposed
    for (const key in user) {
      schema[key] = user[key];
    }

    setSelectedUser(schema);
    setCrudOperationType("CONVERT");
  }, []);

  const closeManagementPanel = useCallback(
    (reload?: boolean) => {
      setCrudOperationType(undefined);
      setSelectedUser(undefined);

      if (Boolean(reload)) {
        getUsers(makeFilters(filters));
        refreshPendingUsers();
      }
    },
    [filters, getUsers, makeFilters, refreshPendingUsers]
  );

  const handleCreateUserBtnClick = useCallback(() => {
    setCrudOperationType(() => "CREATE");
  }, []);

  const handleUsersTableRowClick = useCallback((event, row: RowComponent) => {
    const user = row.getData();
    setSelectedUser(user);
    setCrudOperationType("UPDATE");
  }, []);

  useEffect(() => {
    usersAPI.getUserOnline([]).then((response) => {
      setUsers(response?.data?.rows);
    });
  }, [usersAPI]);

  return (
    <Box
      height={"100%"}
      width={"100%"}
      display={"flex"}
      flexDirection={"column"}
    >
      {Boolean(crudOperationType) && (
        <UserManagementPanel
          operationType={crudOperationType!}
          handleClose={closeManagementPanel}
          existingUser={selectedUser}
        />
      )}
      <Tabs
        textColor="secondary"
        sx={{ display: "flex" }}
        indicatorColor="secondary"
        onChange={handleTabChange}
        value={showTab}
      >
        {TABS.map((tab, index) => (
          <Tab
            key={index}
            value={tab.value}
            sx={{
              fontSize: "0.7vw",
              color: "secondary.main",
              ":checked": "secondary.main",
            }}
            label={
              <Box display={"flex"} gap={1} alignItems={"center"}>
                {tab.icon}
                {tab.label}
                {tab.value === "users" && (users?.length ?? 0) > 0 && ` (${users.length})`}
              </Box>
            }
          />
        ))}
      </Tabs>
      {showTab === "users" ? (
        <UsersList
          onRowClick={handleUsersTableRowClick}
          onClickCreateBtn={handleCreateUserBtnClick}
          users={users}
          filters={filters}
          updateFilters={handleFiltersChange}
        />
      ) : (
        <PendingUsersList
          key={refreshPendingTrigger}
          onRowClick={handleUsersTablePendingRowClick}
        />
      )}
    </Box>
  );
}

const UsersList = ({
  updateFilters,
  filters,
  users,
  onClickCreateBtn,
  onRowClick,
}: UserListProps) => {
  const currentSort = useRef({ column: "id", dir: "asc" });

  const tableOptions = useMemo(() => {
    return {
      layout: "fitColumns",
      initialSort: [currentSort.current],
      columns: [
        { title: "Id", field: "id", sorter: "number" },
        { title: "First Name", field: "firstName" },
        { title: "LastName", field: "lastName" },
        { title: "Email", field: "email" },
        { title: "Active", field: "active" },
        { title: "Type", field: "type" },
        { title: "Owner", field: "owner" },
        { title: "Max Portfolios", field: "productMax" },
        {
          title: "Created at",
          field: "creationTime",
          formatter: (cell) => {
            const timestamp = cell.getValue();
            const date = new Date(timestamp);

            const formatted = date.toLocaleString("it-IT", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });

            return formatted;
          },
        },
      ],
      data: users,
    };
  }, [users]);

  const tableEvents: EventsType = useMemo(
    () => [
      { event: "rowClick", callback: onRowClick },
      {
        event: "dataSorted",
        callback: (sorters, rows) => {
          if (sorters.length) {
            currentSort.current = {
              column: sorters?.[0]?.field,
              dir: sorters?.[0]?.dir,
            };
          }
        },
      },
    ],
    [onRowClick]
  );

  return (
    <Box display={"flex"} minHeight={0} minWidth={0} flexDirection={"column"}>
      <Box display={"flex"} gap={1} alignItems={"center"} p={1} pl={0}>
        <FiltersRow filters={filters} handleChangeFilters={updateFilters} />
        <Box
          display={"flex"}
          gap={1}
          flexDirection={"column"}
          pl={1}
          borderLeft={"1px solid black"}
        >
          <Typography sx={{ visibility: "hidden" }}>Button</Typography>
          <Button
            onClick={onClickCreateBtn}
            startIcon={<AddIcon />}
            size={"small"}
          >
            Create User
          </Button>
        </Box>
      </Box>
      <Box flex={1} minHeight={0}>
        {users.length === 0 ? (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            flex={1}
            height={"50vh"}
          >
            <Typography fontSize={"1vw"}>
              <strong>No user found</strong>
            </Typography>
          </Box>
        ) : (
          <Tabulator events={tableEvents} options={tableOptions} />
        )}
      </Box>
    </Box>
  );
};

const PendingUsersList = ({ onRowClick }) => {
  const [pendingUsers, setPendingUsers] = useState<any>();

  const usersAPI = useMemo(() => new Users(), []);

  const currentSort = useRef({ column: "id", dir: "asc" });

  const searchPendingUsers = useCallback(async () => {
    const reply = await usersAPI.searchPendingUsersOnline();

    setPendingUsers(reply?.data?.rows);
  }, [usersAPI]);

  useEffect(() => {
    searchPendingUsers();
  }, [searchPendingUsers]);

  const tableOptions = useMemo(() => {
    return {
      layout: "fitColumns",
      initialSort: [currentSort.current],
      columns: [
        { title: "Id", field: "id", sorter: "number" },
        { title: "First Name", field: "firstName" },
        { title: "LastName", field: "lastName" },
        { title: "Email", field: "email" },
        {
          title: "Created at",
          field: "creationTime",
          formatter: (cell) => {
            const timestamp = cell.getValue();
            const date = new Date(timestamp);

            const formatted = date.toLocaleString("it-IT", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });

            return formatted;
          },
        },
      ],
      data: pendingUsers,
    };
  }, [pendingUsers]);

  const tableEvents: EventsType = useMemo(
    () => [
      { event: "rowClick", callback: onRowClick },
      {
        event: "dataSorted",
        callback: (sorters, rows) => {
          if (sorters.length) {
            currentSort.current = {
              column: sorters?.[0]?.field,
              dir: sorters?.[0]?.dir,
            };
          }
        },
      },
    ],
    [onRowClick]
  );

  return pendingUsers != null && pendingUsers.length > 0 ? (
    <Box flex={1} display={"flex"} minHeight={0} minWidth={0}>
      <Tabulator events={tableEvents} options={tableOptions} />
    </Box>
  ) : (
    <Box
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
      flex={1}
      height={"50vh"}
    >
      <Typography fontSize={"1vw"}>
        <strong>No user found</strong>
      </Typography>
    </Box>
  );
};

const FiltersRow = ({ handleChangeFilters, filters }: FiltersRowProps) => {
  const updateFilters = useCallback(
    (event, dimension: string, widgets) => {
      const filters: any = {};
      let value = event.target.value;

      for (const widget of widgets) {
        if (widget.dimension !== dimension) {
          filters[widget.dimension] = widget.default;
        } else {
          filters[dimension] = value;
        }
      }

      handleChangeFilters(filters);
    },
    [handleChangeFilters]
  );

  const widgets = useMemo(() => {
    return [
      {
        values: WIDGETS_OPTIONS["accountType"],
        label: "Account Type",
        default: filters["type"],
        dimension: "type",
      },
      {
        values: WIDGETS_OPTIONS["profile"],
        label: "Profile",
        default: filters["profile"],
        dimension: "profile",
      },
      {
        values: WIDGETS_OPTIONS["contractType"],
        label: "Contract Type",
        default: filters["contractType"],
        dimension: "contractType",
      },
      {
        values: WIDGETS_OPTIONS["active"],
        label: "Active",
        default: filters["active"],
        dimension: "active",
      },
      {
        values: WIDGETS_OPTIONS["owner"],
        label: "Owner",
        default: filters["owner"],
        dimension: "owner",
      },
      {
        values: WIDGETS_OPTIONS["portfolioNumber"],
        label: "Portfolios",
        default: filters["productMax"],
        dimension: "productMax",
      },
    ];
  }, [filters]);

  return (
    <Box display={"flex"} gap={1}>
      {widgets.map((widget, index) => (
        <Box key={index} display={"flex"} flexDirection={"column"} gap={0.5}>
          <Typography>{widget.label}</Typography>
          <Select
            onChange={(event) =>
              updateFilters(event, widget.dimension, widgets)
            }
            sx={{
              "& .MuiSelect-select": {
                padding: "6px 12px",
              },
            }}
            size="small"
            value={widget.default}
          >
            {widget.values.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ))}
    </Box>
  );
};

const UserManagementPanel = ({
  handleClose,
  existingUser,
  operationType,
}: CreateNewUserPanelProps) => {
  const [showErrors, setShowErrors] = useState(false);
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(true);
  const [tableReady, setTableReady] = useState(false);
  const [showConfirmDeleteActionModal, setShowConfirmDeleteActionModal] =
    useState(false);
  const [
    showConfirmDeleteFromPendingActionModal,
    setShowConfirmDeleteFromPendingActionModal,
  ] = useState(false);
  const suppressRowSelection = useRef(true);

  const {
    user,
    updateUser,
    errors,
    valid,
    assignedPortfolios,
    changeAssignedPortfolios,
    status,
    createUserService,
    deleteUserService,
    updateUserService,
    convertUserService,
    deleteUserFromPendingList,
  } = usePortfolioUser(operationType, existingUser);
  const auth = useAuth();
  const portfolioPermissionsAPI = useMemo(() => new PortfolioPermissions(), []);
  const feedback = useFeedbackSystem();

  const createNewUser = useCallback(async () => {
    setShowErrors(true);
    try {
      const user = await createUserService();
      handleClose(true);

      feedback.show({
        type: "success",
        message: `${user.firstName} ${user.lastName} user has been created with the id: ${user.id}`,
      });
    } catch (error) {
      feedback.show({
        type: "error",
        message: `The user has not been saved due to an error that occures during saving process`,
      });
      handleClose();
      console.log(error);
    }
    setShowErrors(false);
  }, [createUserService, feedback, handleClose]);

  const errorProps = useCallback(
    (field: keyof typeof errors, helperText: boolean) => {
      if (!showErrors) {
        return {
          error: false,
        };
      }

      const err = {
        error: !errors[field].isValid,
      };

      if (helperText) {
        err["helperText"] = errors[field].message ?? "";
      }

      return err;
    },
    [errors, showErrors]
  );

  const deleteUser = useCallback(() => {
    setShowConfirmDeleteActionModal(true);
  }, []);

  const closeDeleteActionModal = useCallback(() => {
    setShowConfirmDeleteActionModal(false);
  }, []);

  const handleUserDelete = useCallback(async () => {
    try {
      await deleteUserService();
      handleClose(true);
      closeDeleteActionModal();

      feedback.show({
        type: "success",
        message: `${user.firstName} ${user.lastName} user has been removed`,
      });
    } catch (error) {
      feedback.show({
        type: "error",
        message: `The user has not been removed due to an error that occures during the delete process`,
      });
      console.log(error);
      handleClose();
    }
  }, [
    closeDeleteActionModal,
    deleteUserService,
    feedback,
    handleClose,
    user.firstName,
    user.lastName,
  ]);

  const putUser = useCallback(async () => {
    try {
      const user = await updateUserService();
      handleClose(true);

      feedback.show({
        type: "success",
        message: `${user.firstName} ${user.lastName} user has been modified successfully`,
      });
    } catch (error) {
      feedback.show({
        type: "error",
        message: `The user has not been saved due to an error that occures during saving process`,
      });
      console.log(error);
      handleClose();
    }
  }, [feedback, handleClose, updateUserService]);

  const convertUser = useCallback(async () => {
    try {
      const user = await convertUserService();
      handleClose(true);

      feedback.show({
        type: "success",
        message: `${user.firstName} ${user.lastName} user has been created with the id: ${user.id}`,
      });
    } catch (error) {
      feedback.show({
        type: "error",
        message: `The user has not been saved due to an error that occures during saving process`,
      });
      handleClose();
      console.log(error);
    }
  }, [convertUserService, feedback, handleClose]);

  const openDeleteFromPendingPanel = useCallback(() => {
    setShowConfirmDeleteFromPendingActionModal(true);
  }, []);

  const modalsButtons = useMemo(() => {
    const actions: any = [
      {
        name: "Cancel",
        style: { backgroundColor: "black", color: "white" },
        callback: handleClose,
      },
    ];

    if (operationType === "CREATE") {
      actions.unshift({
        name: "Create User",
        disabled: !valid,
        callback: createNewUser,
      });
    } else if (operationType === "UPDATE") {
      actions.unshift(
        ...[
          {
            name: "Update User",
            disabled: !valid,
            callback: putUser,
          },
          {
            name: "Delete User",
            callback: deleteUser,
            style: { backgroundColor: "red", color: "white" },
          },
        ]
      );
    } else if (operationType === "CONVERT") {
      actions.unshift(
        ...[
          {
            name: "Convert User",
            disabled: !valid,
            callback: convertUser,
          },
          {
            name: "Delete From Pending",
            style: { backgroundColor: "red", color: "white" },
            callback: openDeleteFromPendingPanel,
          },
        ]
      );
    }

    return actions;
  }, [
    convertUser,
    createNewUser,
    deleteUser,
    handleClose,
    openDeleteFromPendingPanel,
    operationType,
    putUser,
    valid,
  ]);

  const portfoliosTableRef = useRef<any>(null);
  const portfoliosColumns: ColumnDefinition[] = useMemo(() => {
    return [
      {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        title: "",
        resizable: false,
        frozen: true,
        widthGrow: 0.3,
        hozAlign: "center",
        headerHozAlign: "center",
        headerSort: false,
      },
      {
        title: "ID",
        field: "id",
        widthGrow: 1,
        hozAlign: "left",
      },
      { title: "Name", field: "name", widthGrow: 1, hozAlign: "left" },
      { title: "Method", field: "method", widthGrow: 2, hozAlign: "left" },
    ];
  }, []);
  const portfoliosTableOptions = useMemo(
    () => ({
      columns: portfoliosColumns,
      layout: "fitColumns",
      selectableRows: true,
    }),
    [portfoliosColumns]
  );

  const loadPortfolios = useCallback(async () => {
    setIsLoadingPortfolios(true);

    const table = portfoliosTableRef.current.getTableInstance();
    let objects = await portfolioPermissionsAPI.getObjects(
      "main_develop",
      auth.user.additionalCookies
    );
    objects = objects.map((obj: any) => ({
      id: obj.name,
      name: obj.info.name,
      method: obj.info.method,
    }));

    table.setData(objects);
    setIsLoadingPortfolios(false);
    setTableReady(true);
  }, [auth.user.additionalCookies, portfolioPermissionsAPI]);

  const portfoliosTableEvents: EventsType = useMemo(
    () => [
      {
        event: "tableBuilt",
        callback: loadPortfolios,
      },
      {
        event: "tableDestroyed",
        callback: () => setTableReady(false),
      },
      {
        event: "rowSelectionChanged",
        callback: (data, rows, selected, deselected) => {
          if (!suppressRowSelection.current) {
            changeAssignedPortfolios(
              rows.map((r) => r.getData()).map((d) => d.id)
            );
          }
        },
      },
    ],
    [changeAssignedPortfolios, loadPortfolios]
  );

  useEffect(() => {
    // Apply the initial selected rows based on the user data
    if (tableReady) {
      suppressRowSelection.current = true;
      const table = portfoliosTableRef.current.getTableInstance();
      const tableRows: RowComponent[] = table.getRows();

      if (assignedPortfolios && assignedPortfolios.length) {
        for (const row of tableRows) {
          if (!row.isSelected()) {
            const rowData: any = row.getData();

            if (
              rowData?.id != null &&
              assignedPortfolios.includes(rowData.id)
            ) {
              row.select();
            }
          }
        }
      }

      suppressRowSelection.current = false;
    }
  }, [assignedPortfolios, tableReady]);

  const closeDeleteFromPendingActionModal = useCallback(() => {
    setShowConfirmDeleteFromPendingActionModal(false);
  }, []);

  const wrapDeleteFromPendingList = useCallback(async () => {
    try {
      await deleteUserFromPendingList();
      handleClose(true);
      closeDeleteFromPendingActionModal();

      feedback.show({
        type: "success",
        message: `${user.firstName} ${user.lastName} user has been removed from the pending list`,
      });
    } catch (error) {
      feedback.show({
        type: "error",
        message: `The user has not been removed due to an error that occures during the remove process`,
      });
      console.log(error);
      handleClose();
    }
  }, [
    closeDeleteFromPendingActionModal,
    deleteUserFromPendingList,
    feedback,
    handleClose,
    user.firstName,
    user.lastName,
  ]);

  return (
    <>
      {showConfirmDeleteActionModal && (
        <Modal
          buttonsEnalbed
          buttons={[
            {
              name: "Confirm",
              style: { backgroundColor: "red", color: "white" },
              callback: handleUserDelete,
            },
            { name: "Cancel", callback: closeDeleteActionModal },
          ]}
          headerConfig={{
            headerContent: (
              <Typography>
                <strong>
                  Delete {user.firstName} {user.lastName}
                </strong>
              </Typography>
            ),
          }}
        >
          <Typography>
            Are you sure to delete {user.firstName} {user.lastName}? This action
            is not reversible and all assignated portfolios will be lost.
            Confirm to continue
          </Typography>
        </Modal>
      )}
      {showConfirmDeleteFromPendingActionModal && (
        <Modal
          buttonsEnalbed
          buttons={[
            {
              name: "Confirm",
              style: { backgroundColor: "red", color: "white" },
              callback: wrapDeleteFromPendingList,
            },
            { name: "Cancel", callback: closeDeleteFromPendingActionModal },
          ]}
          headerConfig={{
            headerContent: (
              <Typography>
                <strong>
                  Delete {user.firstName} {user.lastName}
                </strong>
              </Typography>
            ),
          }}
        >
          <Typography>
            Are you sure to delete {user.firstName} {user.lastName} from the
            pending list? This action is not reversible, please confirm to
            continue
          </Typography>
        </Modal>
      )}
      <Modal
        customCss={{ maxWidth: "none", width: "80%" }}
        buttonsEnalbed
        bodyCustomClass={styles.portfolio__user__management__panel}
        buttons={modalsButtons}
        headerConfig={{
          headerContent: (
            <Typography>
              <strong>
                {operationType === "UPDATE"
                  ? `Update ${user.firstName} ${user.lastName}`
                  : "Create new user"}
              </strong>
            </Typography>
          ),
        }}
      >
        {status !== "loading" ? (
          <Box display={"flex"} minHeight={0} gap={1} flex={1}>
            <Box
              sx={{ padding: "0!important" }}
              borderRadius={"6px"}
              border={"1px solid black"}
            >
              <Box p={1} borderBottom={"1px solid black"}>
                <strong>Account</strong>
              </Box>
              <Box display={"flex"} flexDirection={"column"} p={1} gap={2}>
                <Typography>
                  * Please fill all the fields below to activate the Create User
                  button at the end of the panel
                </Typography>
                <Box display={"flex"} gap={1}>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>First Name</strong>
                    </Typography>
                    <TextField
                      {...errorProps("firstName", true)}
                      value={user.firstName}
                      onChange={(event) =>
                        updateUser({
                          type: "FIRST_NAME",
                          payload: event.target.value,
                        })
                      }
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Last Name</strong>
                    </Typography>
                    <TextField
                      {...errorProps("lastName", true)}
                      value={user.lastName}
                      onChange={(event) =>
                        updateUser({
                          type: "LAST_NAME",
                          payload: event.target.value,
                        })
                      }
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Email</strong>
                    </Typography>
                    <TextField
                      {...errorProps("email", true)}
                      value={user.email}
                      onChange={(event) =>
                        updateUser({
                          type: "EMAIL",
                          payload: event.target.value,
                        })
                      }
                    />
                  </Box>
                </Box>

                <Box display={"flex"} gap={1}>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Contact Email</strong>
                    </Typography>
                    <TextField
                      {...errorProps("userName", true)}
                      value={user.userName}
                      onChange={(event) =>
                        updateUser({
                          type: "USER_NAME",
                          payload: event.target.value,
                        })
                      }
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Firm</strong>
                    </Typography>
                    <TextField
                      {...errorProps("firm", true)}
                      value={user.firm ?? ""}
                      onChange={(event) =>
                        updateUser({
                          type: "FIRM",
                          payload: event.target.value,
                        })
                      }
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Profile</strong>
                    </Typography>
                    <Select
                      {...errorProps("profile", false)}
                      value={user.profile ?? "unset"}
                      onChange={(event) =>
                        updateUser({
                          type: "PROFILE",
                          payload: event.target.value as string,
                        })
                      }
                    >
                      {WIDGETS_OPTIONS.profile.map((v, i) => (
                        <MenuItem key={i} value={v.value}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
                <CountrySelect
                  value={user.country}
                  {...errorProps("country", false)}
                  updateFn={updateUser}
                />
                <Box display={"flex"} gap={1}>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Account Type</strong>
                    </Typography>
                    <Select
                      {...errorProps("accountType", false)}
                      value={user.type}
                      onChange={(event) =>
                        updateUser({
                          type: "TYPE",
                          payload: event.target.value as string,
                        })
                      }
                    >
                      {WIDGETS_OPTIONS.accountType.map((v, i) => (
                        <MenuItem key={i} value={v.value}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Contract Type</strong>
                    </Typography>
                    <Select
                      {...errorProps("contractType", false)}
                      value={user.contractType}
                      onChange={(event) =>
                        updateUser({
                          type: "CONTRACT_TYPE",
                          payload: event.target.value as string,
                        })
                      }
                    >
                      {WIDGETS_OPTIONS.contractType.map((v, i) => (
                        <MenuItem key={i} value={v.value}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Owner</strong>
                    </Typography>
                    <Select
                      {...errorProps("owner", false)}
                      value={user.owner}
                      onChange={(event) =>
                        updateUser({
                          type: "OWNER",
                          payload: event.target.value as string,
                        })
                      }
                    >
                      {WIDGETS_OPTIONS.owner.map((v, i) => (
                        <MenuItem key={i} value={v.value}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                <Box display={"flex"} gap={1}>
                  <Box display={"flex"} flexDirection={"column"} gap={1}>
                    <Typography>
                      <strong>Conversion Date</strong>
                    </Typography>
                    <DatePicker
                      value={
                        (user as any).conversionDate_ToIso != null
                          ? dayjs((user as any).conversionDate_ToIso)
                          : undefined
                      }
                      onChange={(newDate) =>
                        updateUser({
                          type: "CONVERSION_DATE",
                          payload: newDate!.toISOString(),
                        })
                      }
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    gap={1}
                  >
                    <Typography>
                      <strong>Billing Start</strong>
                    </Typography>
                    <DatePicker
                      value={
                        (user as any).billingStart_ToIso != null
                          ? dayjs((user as any).billingStart_ToIso)
                          : undefined
                      }
                      onChange={(newDate) =>
                        updateUser({
                          type: "BILLING_START",
                          payload: newDate!.toISOString(),
                        })
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{ padding: "0!important", flex: 1 }}
              borderRadius={"6px"}
              border={"1px solid black"}
              minHeight={0}
              display={"flex"}
              flexDirection={"column"}
            >
              <Box p={1} borderBottom={"1px solid black"}>
                <strong>Appplication</strong>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"column"}
                p={1}
                gap={2}
                minHeight={0}
              >
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={1}
                    alignItems={"center"}
                  >
                    <Typography>
                      <strong>Active</strong>
                    </Typography>
                    <Checkbox
                      defaultChecked={user.active}
                      onChange={(event, checked) =>
                        updateUser({ type: "ACTIVE", payload: checked })
                      }
                    />
                  </Box>
                  <Box display={"flex"} flexDirection={"column"} gap={1}>
                    <Typography>
                      <strong>Number of portfolios</strong>
                    </Typography>
                    <Select
                      {...errorProps("portfolios", false)}
                      value={user.productMax}
                      onChange={(event) =>
                        updateUser({
                          type: "PRODUCT_MAX",
                          payload: event.target.value as number,
                        })
                      }
                    >
                      {WIDGETS_OPTIONS.portfolioNumber.map((v, i) => (
                        <MenuItem key={i} value={v.value}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
                <Box
                  display={"flex"}
                  flex={1}
                  minHeight={0}
                  flexDirection={"column"}
                  gap={1}
                >
                  {user.productMax === assignedPortfolios.length && (
                    <Alert severity="success">
                      You reached the maximum portfolio assignement for this
                      user
                    </Alert>
                  )}
                  {user.productMax > assignedPortfolios.length && (
                    <Alert severity="info">
                      You assigned <strong>{assignedPortfolios.length}</strong>{" "}
                      of <strong>{user.productMax}</strong> portfolios. You
                      still have{" "}
                      <strong>
                        {user.productMax - assignedPortfolios.length}
                      </strong>{" "}
                      assignments available.
                    </Alert>
                  )}
                  {user.productMax < assignedPortfolios.length && (
                    <Alert severity="warning">
                      You exceed the the maximum portfolio assignement for this
                      user
                    </Alert>
                  )}
                  <Box
                    sx={{ display: isLoadingPortfolios ? "flex" : "none" }}
                    flex={1}
                    flexDirection={"column"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={2}
                  >
                    <CircularProgress color="secondary" />
                    <Typography>Loading Portfolios...</Typography>
                  </Box>

                  <Box
                    sx={{ display: isLoadingPortfolios ? "none" : "flex" }}
                    flex={1}
                    minHeight={0}
                  >
                    <Tabulator
                      ref={portfoliosTableRef}
                      options={portfoliosTableOptions}
                      events={portfoliosTableEvents}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ display: isLoadingPortfolios ? "flex" : "none" }}
            flex={1}
            flexDirection={"column"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={2}
          >
            <CircularProgress color="secondary" />
            <Typography>Loading User...</Typography>
          </Box>
        )}
      </Modal>
    </>
  );
};
